import React, { FC, useState } from 'react';
import { Button, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SendIcon } from '~/img';
import { Transaction, Account, Employee, Terminal } from '~/types';
import c from '~/utils/constants';
import SenderAccount from './SenderAccount';
import RecipientAccount from './RecipientAccount';
import SuccessScreen from './SuccessScreen';
import { Drawer, TransactionProcessing } from '~/components';
import AmountAndConsent from './AmountAndConsent';
import { useApi, usePermissions } from '~/hooks';
import { getAccounts } from '~/api/accounts';
import { getAllEmployees } from '~/api/employees';
import { getAllTerminals } from '~/api/terminals';
import { OatConsentData } from './OatConsentData';

interface Props {
  className?: string;
  disabled?: boolean;
  reloadAccounts?: () => void;
}

const OwnAccountTransfer: FC<Props> = (props) => {
  const { disabled, className, reloadAccounts } = props;
  const [t] = useTranslation('oat');

  const [mainState, setMainState] = useState<OatConsentData>({
    ...initialState,
  });
  const [transactions, setTransactions] = useState<Transaction[] | undefined>();

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setMainState({
      ...initialState,
    });
    setTransactions(undefined);
    setOpen(false);
  };
  const hasAccessCreate = usePermissions('business.accounts.transfer');
  if (!hasAccessCreate) return null;

  return (
    <>
      <Button type="primary" onClick={handleOpen} disabled={disabled} size="large">
        <SendIcon />
        {t('Money transfer')}
      </Button>
      <Drawer
        visible={open}
        onClose={handleClose}
        title={t('Money transfer')}
        placement="bottom"
        className={`${className} Oat`}
      >
        <div className="Oat-ContentWrapper">
          {transactions ? (
            <TransactionProcessing
              onComplete={(t) => {
                setTransactions((prev) => (!prev ? prev : [t, prev[1]]));
              }}
              transactionId={transactions?.[0]?.id}
              completeComponent={
                <SuccessScreen transactions={transactions} onClose={handleClose} />
              }
            />
          ) : (
            <DrawerContent
              mainState={mainState}
              setMainState={setMainState}
              setTransactions={(payload) => {
                setTransactions(payload);
                reloadAccounts && reloadAccounts();
              }}
            />
          )}
        </div>
      </Drawer>
    </>
  );
};

interface DrawerContentProps {
  mainState: OatConsentData;
  setMainState: React.Dispatch<React.SetStateAction<OatConsentData>>;
  setTransactions: React.Dispatch<React.SetStateAction<Transaction[] | undefined>>;
}

const DrawerContent = ({ mainState, setMainState, setTransactions }: DrawerContentProps) => {
  const [{ data: accounts, loading: accountsLoading }] = useApi<Account[]>(getAccounts);
  const [{ data: employees, loading: employeesLoading }] = useApi<Employee[]>(getAllEmployees);
  const [{ data: terminals, loading: terminalsLoading }] = useApi<Terminal[]>(getAllTerminals);
  const loading = accountsLoading || employeesLoading || terminalsLoading;

  return (
    <Space direction="vertical" size={24} className="Oat-Content">
      <SenderAccount
        mainState={mainState}
        setMainState={setMainState}
        loading={loading}
        accounts={accounts}
        terminals={terminals}
        employees={employees}
      />
      <RecipientAccount
        mainState={mainState}
        setMainState={setMainState}
        loading={loading}
        accounts={accounts}
        terminals={terminals}
        employees={employees}
      />
      <AmountAndConsent
        setMainState={setMainState}
        mainState={mainState}
        setTransactions={setTransactions}
      />
    </Space>
  );
};

const initialState: OatConsentData = {
  senderAccount: undefined,
  recipientAccount: undefined,
  amount: {
    value: 0,
    currency: '',
  },
};

export default styled(OwnAccountTransfer)`
  .ant-drawer-content,
  .ant-drawer-header {
    background: ${c.layoutBodyBackground};
  }
  .Oat-ContentWrapper {
    display: flex;
    justify-content: center;
    .Oat-Header {
      font-size: 18px;
    }
    .Oat-Content {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      max-width: 760px;
      flex-grow: 1;
      .ant-space-item {
        width: 100%;
      }
    }
  }
  .Oat-TotalAmount {
    font-size: 18px;
    color: ${c.primaryColor};
  }
`;
